import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PaymentStatus } from '../stock/stock.type';
import {
  CreateManualSettlementRequest,
  CreateSettlementRequest,
  DailyPayablesReportRequest,
  DailyPayablesReportResponse,
  GetManualContractPayablesRequest,
  GetManualContractPayablesResponse,
  GetPaymentRequestsRequest,
  GetPaymentRequestsResponse,
  GetSettlementsRequest,
  GetSettlementsResponse,
  ManualContractPayable,
  CreatePaymentMethodTypeRequest,
  PayPaymentRequestResponse,
  PaymentDestination,
  PaymentMethodType,
  PaymentSource,
  PaymentDetails,
  CreatePaymentSourceRequest,
  UpdatePaymentSourceRequest,
  CryptoPayout,
  CryptoPayoutSettings,
  CryptoWallet,
  GetCryptoPayoutsResponse,
  PaymentRequest,
  PaymentRequestDetail,
  Settlement,
  SettlementItems,
  SettlementProof,
  SettlementReportRequest,
  TransferRunPayable,
  UpdatePaymentMethodTypeRequest,
  UpdatePaymentStatusRequest,
} from './payment.type';
import { environment } from '../../environments/environment';

/** The backend wraps every success as {result: ...}. */
interface Envelope<T> { result: T; }

/** The payment module's API: payables, settlements and payment requests. */
@Injectable({ providedIn: 'root' })
export class PaymentService {
  private apiUrl = environment.apiBaseURL + '/payments';

  constructor(private http: HttpClient) { }

  /** Per day: runs and manual buy contracts, each split into paid and unpaid, plus range totals. */
  getDailyPayablesReport(request: DailyPayablesReportRequest = {}): Observable<DailyPayablesReportResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<Envelope<DailyPayablesReportResponse>>(`${this.apiUrl}/reports/daily-payables`, { params })
      .pipe(map(r => r.result));
  }

  /** Admin-only correction of a run's fee snapshot; recomputes payable_amount server-side. */
  updateTransferRunFee(runId: string, feeSnapshot: number): Observable<TransferRunPayable> {
    return this.http
      .patch<Envelope<TransferRunPayable>>(`${this.apiUrl}/transfer-runs/${runId}/fee`, { fee_snapshot: feeSnapshot })
      .pipe(map(r => r.result));
  }

  /**
   * Admin-only direct override of a run's payment status, bypassing the
   * settlement flow. Reverting to NotPayed detaches the run from whatever
   * settlement it belonged to.
   */
  updateTransferRunPaymentStatus(runId: string, paymentStatus: PaymentStatus): Observable<TransferRunPayable> {
    const body: UpdatePaymentStatusRequest = { payment_status: paymentStatus };
    return this.http
      .patch<Envelope<TransferRunPayable>>(`${this.apiUrl}/transfer-runs/${runId}/payment-status`, body)
      .pipe(map(r => r.result));
  }

  // ── Manual settlement ──

  getSettlements(request: GetSettlementsRequest = {}): Observable<GetSettlementsResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<Envelope<GetSettlementsResponse>>(`${this.apiUrl}/settlements`, { params })
      .pipe(map(r => r.result));
  }

  /**
   * multipart/form-data, not JSON, so optional proof image files can ride
   * along in the same request.
   */
  createSettlement(request: CreateSettlementRequest, proofFiles: File[] = []): Observable<Settlement> {
    const form = new FormData();
    form.set('supplier_id', request.supplier_id);
    for (const id of request.run_ids ?? []) form.append('run_ids', id);
    for (const id of request.contract_ids ?? []) form.append('contract_ids', id);
    if (request.note) form.set('note', request.note);
    for (const f of proofFiles) form.append('proof', f);
    return this.http
      .post<Envelope<Settlement>>(`${this.apiUrl}/settlements`, form)
      .pipe(map(r => r.result));
  }

  /** A bonus or the fix for a mistake: an amount and a description, covering no runs or contracts. */
  createManualSettlement(request: CreateManualSettlementRequest, proofFiles: File[] = []): Observable<Settlement> {
    const form = new FormData();
    form.set('supplier_id', request.supplier_id);
    form.set('kind', request.kind);
    form.set('amount', String(request.amount));
    form.set('note', request.note);
    for (const f of proofFiles) form.append('proof', f);
    return this.http
      .post<Envelope<Settlement>>(`${this.apiUrl}/settlements/manual`, form)
      .pipe(map(r => r.result));
  }

  /**
   * The accountant's .xlsx for one day. Built by the backend (it holds the proof
   * images and the covered runs/contracts); the browser only names the day and
   * its own timezone.
   */
  downloadSettlementsReport(request: SettlementReportRequest): Observable<{ blob: Blob; filename: string }> {
    let params = new HttpParams().set('date', request.date).set('tz', request.tz)
      .set('includeProofs', request.includeProofs);
    if (request.supplierId) params = params.set('supplierId', request.supplierId);
    if (request.status) params = params.set('status', request.status);
    if (request.dateBy) params = params.set('dateBy', request.dateBy);
    return this.http
      .get(`${this.apiUrl}/settlements/report`, { params, responseType: 'blob', observe: 'response' })
      .pipe(map(res => {
        const match = /filename="?([^";]+)"?/.exec(res.headers.get('Content-Disposition') ?? '');
        return { blob: res.body as Blob, filename: match?.[1] ?? `settlements-report-${request.date}.xlsx` };
      }));
  }

  /**
   * multipart/form-data: an admin often doesn't have the payment
   * proof until the transfer is actually sent, which is exactly when
   * this is called, so proof images can be attached here too — added to any
   * already attached.
   */
  markSettlementPaid(id: string, payment: PaymentDetails, note?: string, proofFiles: File[] = []): Observable<Settlement> {
    const form = new FormData();
    setPaymentFields(form, payment);
    if (note) form.set('note', note);
    for (const f of proofFiles) form.append('proof', f);
    return this.http
      .patch<Envelope<Settlement>>(`${this.apiUrl}/settlements/${id}/paid`, form)
      .pipe(map(r => r.result));
  }

  /** Removes a settlement that is not paid yet. The runs and contracts it covered go back to unpaid. */
  deleteSettlement(id: string): Observable<void> {
    return this.http
      .delete<Envelope<boolean>>(`${this.apiUrl}/settlements/${id}`)
      .pipe(map(() => undefined));
  }

  /** The transfer runs and manual contracts a settlement pays for. */
  getSettlementItems(id: string): Observable<SettlementItems> {
    return this.http
      .get<Envelope<SettlementItems>>(`${this.apiUrl}/settlements/${id}/items`)
      .pipe(map(r => r.result));
  }

  listSettlementProofs(id: string): Observable<SettlementProof[]> {
    return this.http
      .get<Envelope<SettlementProof[]>>(`${this.apiUrl}/settlements/${id}/proofs`)
      .pipe(map(r => r.result));
  }

  /** Same blob-fetch pattern as PlayerAuctionService.getStockLeaseScreenshot. */
  getSettlementProofImage(settlementId: string, proofId: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/settlements/${settlementId}/proofs/${proofId}`, { responseType: 'blob' });
  }

  /** Adds proof images to a settlement, paid or not. */
  addSettlementProofs(id: string, proofFiles: File[]): Observable<SettlementProof[]> {
    const form = new FormData();
    for (const f of proofFiles) form.append('proof', f);
    return this.http
      .post<Envelope<SettlementProof[]>>(`${this.apiUrl}/settlements/${id}/proofs`, form)
      .pipe(map(r => r.result));
  }

  deleteSettlementProof(settlementId: string, proofId: string): Observable<void> {
    return this.http
      .delete<Envelope<boolean>>(`${this.apiUrl}/settlements/${settlementId}/proofs/${proofId}`)
      .pipe(map(() => undefined));
  }

  // ── Manual contract payables ──

  getManualContractPayables(request: GetManualContractPayablesRequest = {}): Observable<GetManualContractPayablesResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<Envelope<GetManualContractPayablesResponse>>(`${this.apiUrl}/manual-contracts`, { params })
      .pipe(map(r => r.result));
  }

  /**
   * Admin-only direct override of a manual contract's payment status,
   * bypassing the settlement flow. Reverting to NotPayed detaches the contract
   * from whatever settlement it belonged to; an Active contract can't be Payed.
   */
  updateManualContractPaymentStatus(contractId: string, paymentStatus: PaymentStatus): Observable<ManualContractPayable> {
    const body: UpdatePaymentStatusRequest = { payment_status: paymentStatus };
    return this.http
      .patch<Envelope<ManualContractPayable>>(`${this.apiUrl}/manual-contracts/${contractId}/payment-status`, body)
      .pipe(map(r => r.result));
  }

  /** Admin-only correction of a manual contract's fee rate. Blocked once Payed. */
  updateManualContractFee(contractId: string, fee: number): Observable<ManualContractPayable> {
    return this.http
      .patch<Envelope<ManualContractPayable>>(`${this.apiUrl}/manual-contracts/${contractId}/fee`, { fee_snapshot: fee })
      .pipe(map(r => r.result));
  }

  // ── Payment requests ──

  getPaymentRequests(request: GetPaymentRequestsRequest = {}): Observable<GetPaymentRequestsResponse> {
    let params = new HttpParams();
    for (const [k, v] of Object.entries(request)) {
      if (v !== undefined && v !== null && v !== '') params = params.set(k, String(v));
    }
    return this.http
      .get<Envelope<GetPaymentRequestsResponse>>(`${this.apiUrl}/requests`, { params })
      .pipe(map(r => r.result));
  }

  /** The "pay via" names requests were made with — the options of the list's payVia filter. */
  getPaymentRequestPayVia(): Observable<string[]> {
    return this.http
      .get<Envelope<string[]>>(`${this.apiUrl}/requests/pay-via`)
      .pipe(map(r => r.result));
  }

  /** The request with the card to pay to and every run and contract it covers. */
  getPaymentRequest(id: string): Observable<PaymentRequestDetail> {
    return this.http
      .get<Envelope<PaymentRequestDetail>>(`${this.apiUrl}/requests/${id}`)
      .pipe(map(r => r.result));
  }

  /**
   * Pays a request in one step: a settlement for exactly what it covers, already
   * marked paid. expectedAmount is the amount the admin sent; the backend refuses
   * anything but the request's own amount, and refuses a second payment.
   */
  payPaymentRequest(id: string, expectedAmount: number, payment: PaymentDetails, note: string, proofFiles: File[] = []): Observable<PayPaymentRequestResponse> {
    const form = new FormData();
    form.set('expected_amount', String(expectedAmount));
    setPaymentFields(form, payment);
    if (note) form.set('note', note);
    for (const f of proofFiles) form.append('proof', f);
    return this.http
      .post<Envelope<PayPaymentRequestResponse>>(`${this.apiUrl}/requests/${id}/pay`, form)
      .pipe(map(r => r.result));
  }

  /**
   * Updates a pending request whose items only changed value to what they are
   * worth now, so it can be paid. expectedAmount is the new amount the admin was
   * shown; if the items moved again the backend changes nothing (409).
   */
  updatePaymentRequestAmount(id: string, expectedAmount: number): Observable<PaymentRequestDetail> {
    return this.http
      .patch<Envelope<PaymentRequestDetail>>(`${this.apiUrl}/requests/${id}/amount`, { expected_amount: expectedAmount })
      .pipe(map(r => r.result));
  }

  /** Only a pending request can be cancelled; doing so does not reset the supplier's cooldown. */
  cancelPaymentRequest(id: string): Observable<PaymentRequest> {
    return this.http
      .patch<Envelope<PaymentRequest>>(`${this.apiUrl}/requests/${id}/cancel`, {})
      .pipe(map(r => r.result));
  }

  /** Declines a pending request; the reason is shown to the supplier, who may then ask again after the shorter rejected cooldown. */
  /**
   * Sends the request's USDT payout from the hot wallet. expectedPayoutAmount
   * is the amount the admin was shown; 409 if it no longer matches.
   */
  sendCryptoPayout(requestId: string, expectedPayoutAmount: string): Observable<CryptoPayout> {
    return this.http
      .post<Envelope<CryptoPayout>>(`${this.apiUrl}/requests/${requestId}/crypto-payout`, { expected_payout_amount: expectedPayoutAmount })
      .pipe(map(r => r.result));
  }

  /** Only a payout still Queued can be cancelled. */
  cancelCryptoPayout(id: string): Observable<CryptoPayout> {
    return this.http.post<Envelope<CryptoPayout>>(`${this.apiUrl}/crypto-payouts/${id}/cancel`, {}).pipe(map(r => r.result));
  }

  getCryptoPayouts(pageIndex = 0, pageSize = 20, status = ''): Observable<GetCryptoPayoutsResponse> {
    let params = new HttpParams().set('pageIndex', pageIndex).set('pageSize', pageSize);
    if (status) params = params.set('status', status);
    return this.http.get<Envelope<GetCryptoPayoutsResponse>>(`${this.apiUrl}/crypto-payouts`, { params }).pipe(map(r => r.result));
  }

  getCryptoWallet(): Observable<CryptoWallet> {
    return this.http.get<Envelope<CryptoWallet>>(`${this.apiUrl}/crypto-wallet`).pipe(map(r => r.result));
  }

  getCryptoPayoutSettings(): Observable<CryptoPayoutSettings> {
    return this.http
      .get<Envelope<CryptoPayoutSettings>>(`${environment.apiBaseURL}/settings/crypto-payouts`)
      .pipe(map(r => r.result));
  }

  /** Omitted fields keep their value. */
  updateCryptoPayoutSettings(request: Partial<CryptoPayoutSettings>): Observable<CryptoPayoutSettings> {
    return this.http
      .put<Envelope<CryptoPayoutSettings>>(`${environment.apiBaseURL}/settings/crypto-payouts`, request)
      .pipe(map(r => r.result));
  }

  rejectPaymentRequest(id: string, reason: string): Observable<PaymentRequest> {
    return this.http
      .patch<Envelope<PaymentRequest>>(`${this.apiUrl}/requests/${id}/reject`, { reason })
      .pipe(map(r => r.result));
  }

  /** Sets the admin's note, which the supplier reads. An empty note clears it. */
  updatePaymentRequestNote(id: string, note: string): Observable<PaymentRequest> {
    return this.http
      .patch<Envelope<PaymentRequest>>(`${this.apiUrl}/requests/${id}/note`, { note })
      .pipe(map(r => r.result));
  }

  // ── Payout methods and destinations ──

  /** Every payout method, active or not (the admin's view). */
  getPaymentMethodTypes(): Observable<PaymentMethodType[]> {
    return this.http.get<Envelope<PaymentMethodType[]>>(`${this.apiUrl}/method-types`).pipe(map(r => r.result));
  }

  createPaymentMethodType(request: CreatePaymentMethodTypeRequest): Observable<PaymentMethodType> {
    return this.http.post<Envelope<PaymentMethodType>>(`${this.apiUrl}/method-types`, request).pipe(map(r => r.result));
  }

  updatePaymentMethodType(id: string, request: UpdatePaymentMethodTypeRequest): Observable<PaymentMethodType> {
    return this.http.patch<Envelope<PaymentMethodType>>(`${this.apiUrl}/method-types/${id}`, request).pipe(map(r => r.result));
  }

  /** Fetches a wallet method's rate from its provider now; 502 with the provider's message if it fails. */
  refreshPaymentMethodRate(id: string): Observable<PaymentMethodType> {
    return this.http.post<Envelope<PaymentMethodType>>(`${this.apiUrl}/method-types/${id}/refresh-rate`, {}).pipe(map(r => r.result));
  }

  /** 'Manual' plus every exchange-rate provider the server has registered. */
  getRateSources(): Observable<string[]> {
    return this.http.get<Envelope<string[]>>(`${this.apiUrl}/rate-sources`).pipe(map(r => r.result));
  }

  /** Refused (409) while any supplier has a destination under it; deactivate it instead. */
  deletePaymentMethodType(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/method-types/${id}`);
  }

  /** One supplier's saved payout destinations. */
  getPaymentDestinations(supplierId: string): Observable<PaymentDestination[]> {
    const params = new HttpParams().set('supplierId', supplierId);
    return this.http.get<Envelope<PaymentDestination[]>>(`${this.apiUrl}/destinations`, { params }).pipe(map(r => r.result));
  }

  // ── Payment source accounts (our own accounts payouts leave from) ──

  getPaymentSources(activeOnly = false): Observable<PaymentSource[]> {
    const params = activeOnly ? new HttpParams().set('active', 'true') : undefined;
    return this.http.get<Envelope<PaymentSource[]>>(`${this.apiUrl}/source-accounts`, { params }).pipe(map(r => r.result));
  }

  createPaymentSource(request: CreatePaymentSourceRequest): Observable<PaymentSource> {
    return this.http.post<Envelope<PaymentSource>>(`${this.apiUrl}/source-accounts`, request).pipe(map(r => r.result));
  }

  updatePaymentSource(id: string, request: UpdatePaymentSourceRequest): Observable<PaymentSource> {
    return this.http.patch<Envelope<PaymentSource>>(`${this.apiUrl}/source-accounts/${id}`, request).pipe(map(r => r.result));
  }

  /** Refused once any settlement was paid from it; deactivate it instead. */
  deletePaymentSource(id: string): Observable<void> {
    return this.http.delete<Envelope<string>>(`${this.apiUrl}/source-accounts/${id}`).pipe(map(() => undefined));
  }

  /** Records or corrects how a paid settlement was paid. */
  updateSettlementPayment(id: string, payment: PaymentDetails): Observable<Settlement> {
    return this.http
      .patch<Envelope<Settlement>>(`${this.apiUrl}/settlements/${id}/payment`, payment)
      .pipe(map(r => r.result));
  }
}

function setPaymentFields(form: FormData, payment: PaymentDetails): void {
  form.set('source_account_id', payment.source_account_id);
  if (payment.bank_fee) form.set('bank_fee', String(payment.bank_fee));
  if (payment.transaction_ref) form.set('transaction_ref', payment.transaction_ref.trim());
}
